import React, { useMemo } from 'react';
import { Container, Row, Col } from 'reactstrap';
import { Link, useNavigate } from 'react-router-dom';
import { CellContext } from '@tanstack/react-table';
import Breadcrumbs from 'components/Common/Breadcrumb';
import { DataTable } from 'components/DataTable';
import { DataTableColumn, TableQueryParams, PaginatedResponse } from 'types/table.types';
import { SHOP_PATHS } from 'routes/routesConstants';
import { useShop } from 'context/shopContext';
import { API_ENDPOINTS } from 'http-client/httpConstants';
import { DataTableApiService } from 'services/api/tableApi';
import { formatFullDate, formatRelativeDate } from 'utils/dateFormatter';

interface PurchaseInvoice {
  id: string;
  invoiceNumber: string;
  invoiceDate: string;
  supplierName: string;
  totalAmount: number;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

const PurchaseInvoiceList: React.FC = () => {
  const { shop } = useShop();
  const { id: shopId } = shop!;
  const navigate = useNavigate();

  /**
   * Define columns for the data table
   */
  const columns = useMemo<DataTableColumn<PurchaseInvoice>[]>(
    () => [
      {
        accessorKey: 'invoiceNumber',
        id: 'invoiceNumber',
        header: 'Invoice Number',
        enableSorting: true,
        enableFiltering: true,
        cell: (info: CellContext<PurchaseInvoice, unknown>) => (
          <Link
            to={SHOP_PATHS.PURCHASE_INVOICE_DETAIL.replace(':shopId', shopId || '').replace(
              ':invoiceId',
              info.row.original.id,
            )}
            className="text-dark fw-bold"
          >
            {info.getValue() as string}
          </Link>
        ),
      },
      {
        accessorKey: 'invoiceDate',
        id: 'invoiceDate',
        header: 'Date',
        enableSorting: true,
        enableFiltering: true,
        cell: (info: CellContext<PurchaseInvoice, unknown>) => {
          return formatFullDate(info.getValue() as string, false);
        },
      },
      {
        accessorKey: 'supplierName',
        id: 'supplierName',
        header: 'Supplier',
        enableSorting: true,
        enableFiltering: true,
        cell: (info: CellContext<PurchaseInvoice, unknown>) => (
          <span className="fw-medium">{info.getValue() as string}</span>
        ),
      },
      {
        accessorKey: 'totalAmount',
        id: 'totalAmount',
        header: 'Total Amount',
        enableSorting: true,
        enableFiltering: true,
        cell: (info: CellContext<PurchaseInvoice, unknown>) => {
          const amount = info.getValue() as number;
          return <span className="fw-bold text-success">${amount?.toFixed(2)}</span>;
        },
      },
      {
        accessorKey: 'isActive',
        id: 'isActive',
        header: 'Status',
        enableSorting: true,
        enableFiltering: true,
        filterType: 'checkbox',
        cell: (info: CellContext<PurchaseInvoice, unknown>) => {
          const isActive = info.getValue() as boolean;
          return (
            <span className={`badge ${isActive ? 'bg-success' : 'bg-danger'}`}>
              {isActive ? 'Active' : 'Inactive'}
            </span>
          );
        },
      },
      {
        accessorKey: 'updatedAt',
        id: 'updatedAt',
        header: 'Last Updated',
        enableSorting: true,
        enableFiltering: false,
        cell: (info: CellContext<PurchaseInvoice, unknown>) => {
          const row = info.row.original;
          const dateToUse = row.updatedAt || row.createdAt;
          const { text, isStale } = formatRelativeDate(dateToUse);
          return <span className={`small ${isStale ? 'text-danger' : 'text-muted'}`}>{text}</span>;
        },
      },
      {
        accessorKey: 'actions',
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        enableFiltering: false,
        cell: (info: CellContext<PurchaseInvoice, unknown>) => {
          const row = info.row.original;
          return (
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-primary"
                onClick={() => {
                  const path = SHOP_PATHS.EDIT_PURCHASE_INVOICE.replace(
                    ':shopId',
                    shopId || '',
                  ).replace(':invoiceId', row.id);
                  navigate(path);
                }}
              >
                <i className="ri-edit-line"></i> Edit
              </button>
              <button
                className="btn btn-sm btn-danger"
                onClick={() => {
                  if (window.confirm(`Delete invoice "${row.invoiceNumber}"?`)) {
                    console.log('Delete invoice:', row.id);
                    // TODO: Implement delete
                  }
                }}
              >
                <i className="ri-delete-bin-line"></i> Delete
              </button>
            </div>
          );
        },
      },
    ],
    [shopId, navigate],
  );

  /**
   * Fetch handler for server-side data
   */
  const handleFetchData = async (
    params: TableQueryParams,
  ): Promise<PaginatedResponse<PurchaseInvoice>> => {
    // Add shopId to filter params if needed by the API
    const filterParams = {
      ...params,
      filters: {
        ...params.filters,
        shopId,
      },
    };
    return DataTableApiService.fetchPaginatedData<PurchaseInvoice>(
      API_ENDPOINTS.TENANT.PURCHASES.PAGINATED.replace(':shopId', shopId || ''),
      filterParams,
    );
  };

  /**
   * Add invoice handler
   */
  const handleAddInvoice = () => {
    navigate(SHOP_PATHS.PURCHASE_ENTRY.replace(':shopId', shopId || ''));
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col lg="12">
            <div className="mb-3 d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Purchase Invoices</h5>
              <button className="btn btn-success" onClick={handleAddInvoice}>
                <i className="ri-add-line"></i> Add Purchase Invoice
              </button>
            </div>

            <DataTable
              columns={columns}
              queryKey={API_ENDPOINTS.TENANT.PURCHASES.PAGINATED.replace(':shopId', shopId || '')}
              onFetchData={handleFetchData}
              enableSearch={true}
              enableFiltering={true}
              enablePagination={true}
              enableSorting={true}
              enableColumnVisibility={true}
              enableColumnReorder={false}
              enableRowSelection={false}
              enableBulkActions={false}
              enableExport={true}
              pageSizeOptions={[10, 20, 50, 100]}
              defaultPageSize={10}
              columnVisibilityStorageKey="purchase_invoices_visibility"
              columnOrderStorageKey="purchase_invoices_order"
              title=""
              emptyMessage="No purchase invoices found. Create one to get started."
              striped={true}
              hover={true}
              bordered={true}
              responsive={true}
            />
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default PurchaseInvoiceList;
